import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { notification } from "antd";
import moment from "moment";
import { saveSwmsForm } from "../../../../../../../actions/v2/JobSwmsActions";
import { getStorage } from "../../../../../../../utils/common";
import { ADMIN_DETAILS } from "../../../../../../../dataProvider/constant";
import { Strings } from "../../../../../../../dataProvider/localize";
import { buildJsaFormData, cloneJsaActivities } from "./jsaSectionUtils";

export default function useJsaSectionState({
  activities,
  swmsData,
  approvalStatus,
  jobId,
  swmsId,
  onClose,
  onJsaActivitiesChange,
}) {
  const dispatch = useDispatch();

  const isJSASectionDisabled = approvalStatus && approvalStatus.toString().toLowerCase() === "draft" ? false : true;

  const user = useMemo(() => {
    try {
      const details = JSON.parse(getStorage(ADMIN_DETAILS));
      return details || null;
    } catch (err) {
      return null;
    }
  }, []);

  const userName = useMemo(() => {
    if (!user) {
      return null;
    }

    const fullName = `${user.name || ""} ${user.last_name || ""}`.trim();
    return fullName || user.user_name || null;
  }, [user]);

  const markActivityEdited = useCallback((updated, actIndex) => {
    if (!updated[actIndex]) {
      return updated;
    }

    updated[actIndex].edited = true;
    updated[actIndex].modified_by = user ? user.user_name : null;
    updated[actIndex].modified_by_name = userName;
    updated[actIndex].modified_at = moment.utc().toISOString();

    return updated;
  }, [user, userName]);

  const [activitiesData, setActivitiesData] = useState(() => cloneJsaActivities(activities));
  const [jsaV2Updated, setJsaV2Updated] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [modifiedActs, setModifiedActs] = useState({});
  const [toggledActivities, setToggledActivities] = useState({});
  const [compKey, setCompKey] = useState(0);

  const originalActivitiesRef = useRef(cloneJsaActivities(activities));
  const ignoreNextPropSyncRef = useRef(false);

  useEffect(() => {
    if (ignoreNextPropSyncRef.current) {
      ignoreNextPropSyncRef.current = false;
      return;
    }

    if (!jsaV2Updated) {
      const clonedActivities = cloneJsaActivities(activities);
      setActivitiesData(clonedActivities);
      originalActivitiesRef.current = clonedActivities;
    }
  }, [activities, jsaV2Updated]);

  const onV2ActUpdate = useCallback((data) => {
    if (isJSASectionDisabled) return;
    if (!data || !data.hasOwnProperty("actIndex") || data.actIndex < 0 || !data.key) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      const activity = updated[data.actIndex];

      if (!activity) {
        return prevActivities;
      }

      if (data.key === "activity_name") {
        activity[data.key] = data.value;
        markActivityEdited(updated, data.actIndex);
        return updated;
      }

      if (data.hasOwnProperty("actionIndex") && data.actionIndex >= 0) {
        const action = activity.fm_swms_actions_v2 && activity.fm_swms_actions_v2[data.actionIndex];
        if (!action) {
          return prevActivities;
        }

        if (!data.hasOwnProperty("hazardIndex") || data.hazardIndex < 0) {
          action[data.key] = data.value;
          markActivityEdited(updated, data.actIndex);
          return updated;
        }

        const hazard = action.fm_swms_activities_hazards_v2 && action.fm_swms_activities_hazards_v2[data.hazardIndex];
        if (!hazard) {
          return prevActivities;
        }

        if (data.key === "hazard_name") {
          hazard[data.key] = data.value;
          markActivityEdited(updated, data.actIndex);
          return updated;
        }

        if (data.key === "consequences_name" && data.hasOwnProperty("consequenceIndex") && data.consequenceIndex >= 0) {
          const consequence = hazard.fm_swms_hazards_consequences_v2
            && hazard.fm_swms_hazards_consequences_v2[data.consequenceIndex];

          if (!consequence) {
            return prevActivities;
          }

          consequence[data.key] = data.value;
          markActivityEdited(updated, data.actIndex);
          return updated;
        }
      }

      return prevActivities;
    });
    setModifiedActs((prev) => ({ ...prev, [data.actIndex]: true }));
    setJsaV2Updated(true);
  }, [isJSASectionDisabled, markActivityEdited]);

  const onActivityDelete = useCallback((activityIndex) => {
    if (isJSASectionDisabled) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      if (!updated[activityIndex]) {
        return prevActivities;
      }

      updated[activityIndex].toggled_active = 0;
      markActivityEdited(updated, activityIndex);
      return updated;
    });
    setToggledActivities((prev) => ({ ...prev, [activityIndex]: true }));
    setJsaV2Updated(true);
  }, [isJSASectionDisabled, markActivityEdited]);

  const onActionDelete = useCallback((activityIndex, actionIndex) => {
    if (isJSASectionDisabled) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      if (updated[activityIndex] && updated[activityIndex].fm_swms_actions_v2) {
        updated[activityIndex].fm_swms_actions_v2.splice(actionIndex, 1);
        markActivityEdited(updated, activityIndex);
        return updated;
      }
      return prevActivities;
    });
    setModifiedActs((prev) => ({ ...prev, [activityIndex]: true }));
    setJsaV2Updated(true);
  }, [isJSASectionDisabled, markActivityEdited]);

  const onHazardDelete = useCallback((activityIndex, actionIndex, hazardIndex) => {
    if (isJSASectionDisabled) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      const hazards = updated[activityIndex]
        && updated[activityIndex].fm_swms_actions_v2
        && updated[activityIndex].fm_swms_actions_v2[actionIndex]
        && updated[activityIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2;

      if (hazards) {
        hazards.splice(hazardIndex, 1);
        markActivityEdited(updated, activityIndex);
        return updated;
      }
      return prevActivities;
    });
    setModifiedActs((prev) => ({ ...prev, [activityIndex]: true }));
    setJsaV2Updated(true);
  }, [isJSASectionDisabled, markActivityEdited]);

  const onConsDelete = useCallback((activityIndex, actionIndex, hazardIndex, consIndex) => {
    if (isJSASectionDisabled) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      const consequencesList = updated[activityIndex]
        && updated[activityIndex].fm_swms_actions_v2
        && updated[activityIndex].fm_swms_actions_v2[actionIndex]
        && updated[activityIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2
        && updated[activityIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2[hazardIndex]
        && updated[activityIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2[hazardIndex].fm_swms_hazards_consequences_v2;

      if (consequencesList) {
        consequencesList.splice(consIndex, 1);
        markActivityEdited(updated, activityIndex);
        return updated;
      }
      return prevActivities;
    });
    setModifiedActs((prev) => ({ ...prev, [activityIndex]: true }));
    setJsaV2Updated(true);
  }, [isJSASectionDisabled, markActivityEdited]);

  const onActivityAdd = useCallback(() => {
    if (isJSASectionDisabled) return;

    setActivitiesData((prevActivities) => {
      const timestamp = Date.now();
      return [
        ...cloneJsaActivities(prevActivities),
        {
          activity_name: "",
          activity_id: timestamp,
          toggled_active: 1,
          edited: true,
          created_by: user ? user.user_name : null,
          created_at: moment.utc().toISOString(),
          created_by_name: userName,
          owner: user ? user.user_name : null,
          owner_name: userName,
          modified_by: null,
          modified_by_name: null,
          modified_at: null,
          next_review_date: null,
          approver: null,
          approver_name: null,
          approved_at: null,
          org_id: user && user.organisation && user.organisation.id ? user.organisation.id : null,
          status: "created",
          submitted_at: null,
          fm_swms_actions_v2: [{
            action_name: "",
            control_measures: "",
            persons_in_control: null,
            action_id: timestamp + 1,
            after_consequence: null,
            before_consequence: null,
            after_likelihood: null,
            before_likelihood: null,
            irRisk: "",
            rrRisk: "",
            initial_risk: null,
            residual_risk: null,
            fm_swms_activities_hazards_v2: [{
              hazard_name: "",
              hazard_id: timestamp + 2,
              fm_swms_hazards_consequences_v2: [{
                consequences_name: "",
                consequence_id: timestamp + 3,
              }]
            }]
          }],
          history: [{
            status: "created",
            description: "Activity created.",
            created_by: user ? user.user_name : null,
            created_by_name: userName,
            created_at: moment.utc().toISOString()
          }]
        }
      ];
    });
    setJsaV2Updated(true);
  }, [isJSASectionDisabled, user, userName]);

  const onActionAdd = useCallback((activityIndex) => {
    if (isJSASectionDisabled) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      if (updated[activityIndex]) {
        if (!updated[activityIndex].fm_swms_actions_v2) {
          updated[activityIndex].fm_swms_actions_v2 = [];
        }

        const timestamp = Date.now();
        updated[activityIndex].fm_swms_actions_v2.push({
          action_name: "",
          control_measures: "",
          persons_in_control: null,
          action_id: timestamp,
          after_consequence: null,
          before_consequence: null,
          after_likelihood: null,
          before_likelihood: null,
          irRisk: "",
          rrRisk: "",
          initial_risk: null,
          residual_risk: null,
          fm_swms_activities_hazards_v2: [{
            hazard_name: "",
            hazard_id: timestamp + 1,
            fm_swms_hazards_consequences_v2: [{
              consequences_name: "",
              consequence_id: timestamp + 2,
            }]
          }]
        });
        markActivityEdited(updated, activityIndex);
        return updated;
      }
      return prevActivities;
    });
    setModifiedActs((prev) => ({ ...prev, [activityIndex]: true }));
    setJsaV2Updated(true);
  }, [isJSASectionDisabled, markActivityEdited]);

  const onHazardAdd = useCallback((activityIndex, actionIndex) => {
    if (isJSASectionDisabled) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      const action = updated[activityIndex]
        && updated[activityIndex].fm_swms_actions_v2
        && updated[activityIndex].fm_swms_actions_v2[actionIndex];

      if (action) {
        if (!action.fm_swms_activities_hazards_v2) {
          action.fm_swms_activities_hazards_v2 = [];
        }

        const timestamp = Date.now();
        action.fm_swms_activities_hazards_v2.push({
          hazard_name: "",
          hazard_id: timestamp,
          fm_swms_hazards_consequences_v2: [{
            consequences_name: "",
            consequence_id: timestamp + 1,
          }]
        });
        markActivityEdited(updated, activityIndex);
        return updated;
      }
      return prevActivities;
    });
    setModifiedActs((prev) => ({ ...prev, [activityIndex]: true }));
    setJsaV2Updated(true);
  }, [isJSASectionDisabled, markActivityEdited]);

  const onConsequenceAdd = useCallback((activityIndex, actionIndex, hazardIndex) => {
    if (isJSASectionDisabled) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      const hazard = updated[activityIndex]
        && updated[activityIndex].fm_swms_actions_v2
        && updated[activityIndex].fm_swms_actions_v2[actionIndex]
        && updated[activityIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2
        && updated[activityIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2[hazardIndex];

      if (hazard) {
        if (!hazard.fm_swms_hazards_consequences_v2) {
          hazard.fm_swms_hazards_consequences_v2 = [];
        }

        hazard.fm_swms_hazards_consequences_v2.push({
          consequences_name: "",
          consequence_id: Date.now(),
        });
        markActivityEdited(updated, activityIndex);
        return updated;
      }
      return prevActivities;
    });
    setModifiedActs((prev) => ({ ...prev, [activityIndex]: true }));
    setJsaV2Updated(true);
  }, [isJSASectionDisabled, markActivityEdited]);

  const onMoveActivity = useCallback((fromIndex, toIndex) => {
    if (isJSASectionDisabled) return;
    if (fromIndex === toIndex) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      const movedActivity = updated.splice(fromIndex, 1)[0];
      updated.splice(toIndex, 0, movedActivity);
      return updated;
    });
  }, [isJSASectionDisabled]);

  const onMoveAction = useCallback((activityIndex, fromIndex, toIndex) => {
    if (isJSASectionDisabled) return;
    if (fromIndex === toIndex) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      if (updated[activityIndex] && updated[activityIndex].fm_swms_actions_v2) {
        const actions = updated[activityIndex].fm_swms_actions_v2;
        const movedAction = actions.splice(fromIndex, 1)[0];
        actions.splice(toIndex, 0, movedAction);
        markActivityEdited(updated, activityIndex);
        return updated;
      }
      return prevActivities;
    });
  }, [isJSASectionDisabled, markActivityEdited]);

  const onMoveHazard = useCallback((activityIndex, actionIndex, fromIndex, toIndex) => {
    if (isJSASectionDisabled) return;
    if (fromIndex === toIndex) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      const hazards = updated[activityIndex]
        && updated[activityIndex].fm_swms_actions_v2
        && updated[activityIndex].fm_swms_actions_v2[actionIndex]
        && updated[activityIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2;

      if (hazards) {
        const movedHazard = hazards.splice(fromIndex, 1)[0];
        hazards.splice(toIndex, 0, movedHazard);
        markActivityEdited(updated, activityIndex);
        return updated;
      }
      return prevActivities;
    });
    setModifiedActs((prev) => ({ ...prev, [activityIndex]: true }));
    setJsaV2Updated(true);
  }, [isJSASectionDisabled, markActivityEdited]);

  const onMoveConsequences = useCallback((activityIndex, actionIndex, hazardIndex, fromIndex, toIndex) => {
    if (isJSASectionDisabled) return;
    if (fromIndex === toIndex) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      const consequencesList = updated[activityIndex]
        && updated[activityIndex].fm_swms_actions_v2
        && updated[activityIndex].fm_swms_actions_v2[actionIndex]
        && updated[activityIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2
        && updated[activityIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2[hazardIndex]
        && updated[activityIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2[hazardIndex].fm_swms_hazards_consequences_v2;

      if (consequencesList) {
        const movedConsequence = consequencesList.splice(fromIndex, 1)[0];
        consequencesList.splice(toIndex, 0, movedConsequence);
        markActivityEdited(updated, activityIndex);
        return updated;
      }

      return prevActivities;
    });
    setModifiedActs((prev) => ({ ...prev, [activityIndex]: true }));
    setJsaV2Updated(true);
  }, [isJSASectionDisabled, markActivityEdited]);

  const onRiskSave = useCallback((data) => {
    if (isJSASectionDisabled) return;
    if (!data || !data.hasOwnProperty("actIndex") || data.actIndex < 0 || !data.hasOwnProperty("actionIndex") || data.actionIndex < 0 || !data.type) return;

    setActivitiesData((prevActivities) => {
      const updated = cloneJsaActivities(prevActivities);
      const action = updated[data.actIndex]
        && updated[data.actIndex].fm_swms_actions_v2
        && updated[data.actIndex].fm_swms_actions_v2[data.actionIndex];

      if (!action) {
        return prevActivities;
      }

      if (data.type === "before") {
        if (data.selectedLike) {
          action.before_likelihood = {
            id: data.selectedLike.value,
            name: data.selectedLike.label,
            value: data.selectedLike.like_value
          };
        }
        if (data.selectedCons) {
          action.before_consequence = {
            id: data.selectedCons.value,
            name: data.selectedCons.label,
            value: data.selectedCons.cons_value
          };
        }
        if (data.riskValue !== undefined) {
          action.initial_risk = data.riskValue;
        }
        if (data.riskColor) {
          action.irRisk = data.riskColor;
        }
      }

      if (data.type === "after") {
        if (data.selectedLike) {
          action.after_likelihood = {
            id: data.selectedLike.value,
            name: data.selectedLike.label,
            value: data.selectedLike.like_value
          };
        }
        if (data.selectedCons) {
          action.after_consequence = {
            id: data.selectedCons.value,
            name: data.selectedCons.label,
            value: data.selectedCons.cons_value
          };
        }
        if (data.riskValue !== undefined) {
          action.residual_risk = data.riskValue;
        }
        if (data.riskColor) {
          action.rrRisk = data.riskColor;
        }
      }

      markActivityEdited(updated, data.actIndex);
      return updated;
    });
    setModifiedActs((prev) => ({ ...prev, [data.actIndex]: true }));
    setJsaV2Updated(true);
  }, [isJSASectionDisabled, markActivityEdited]);

  const onDrop = useCallback((activityId = null) => {
    setJsaV2Updated(true);
    if (activityId) {
      setModifiedActs((prev) => ({ ...prev, [activityId]: true }));
    }
  }, []);

  const onJSADataAdd = useCallback((data) => {
    if (isJSASectionDisabled) return;

    if (data.type && data.hasOwnProperty("actIndex") && data.actIndex >= 0) {
      if (data.type === "action") {
        onActionAdd(data.actIndex);
      }
      if (data.hasOwnProperty("actionIndex") && data.actionIndex >= 0) {
        if (data.type === "hazard") {
          onHazardAdd(data.actIndex, data.actionIndex);
        }
        if (data.type === "consequence") {
          onConsequenceAdd(data.actIndex, data.actionIndex, data.hazardIndex);
        }
      }
    }
  }, [isJSASectionDisabled, onActionAdd, onConsequenceAdd, onHazardAdd]);

  const onJSADataDelete = useCallback((data) => {
    if (isJSASectionDisabled) return;

    if (data.type && data.hasOwnProperty("actIndex") && data.actIndex >= 0) {
      if (data.type === "activity") {
        onActivityDelete(data.actIndex);
      }
      if (data.hasOwnProperty("actionIndex") && data.actionIndex >= 0) {
        if (data.type === "action") {
          onActionDelete(data.actIndex, data.actionIndex);
        }
        if (data.hasOwnProperty("hazardIndex") && data.hazardIndex >= 0) {
          if (data.type === "hazard") {
            onHazardDelete(data.actIndex, data.actionIndex, data.hazardIndex);
          }
          if (data.type === "consequence" && data.hasOwnProperty("consIndex") && data.consIndex >= 0) {
            onConsDelete(data.actIndex, data.actionIndex, data.hazardIndex, data.consIndex);
          }
        }
      }
    }
  }, [isJSASectionDisabled, onActivityDelete, onActionDelete, onConsDelete, onHazardDelete]);

  const onMoveRouter = useCallback((data) => {
    if (isJSASectionDisabled) return;

    if (data.hasOwnProperty("actIndex") && data.actIndex >= 0) {
      if (data.type === "action" && data.hasOwnProperty("fromIndex") && data.hasOwnProperty("toIndex")) {
        onMoveAction(data.actIndex, data.fromIndex, data.toIndex);
      }
      if (data.hasOwnProperty("actionIndex") && data.actionIndex >= 0) {
        if (data.type === "hazard") {
          onMoveHazard(data.actIndex, data.actionIndex, data.fromIndex, data.toIndex);
        }
        if (data.hasOwnProperty("hazardIndex") && data.hazardIndex >= 0 && data.type === "consequence") {
          onMoveConsequences(data.actIndex, data.actionIndex, data.hazardIndex, data.fromIndex, data.toIndex);
        }
      }
    }
  }, [isJSASectionDisabled, onMoveAction, onMoveHazard, onMoveConsequences]);

  const onCancelChanges = useCallback(() => {
    setActivitiesData(cloneJsaActivities(originalActivitiesRef.current));
    setJsaV2Updated(false);
    setModifiedActs({});
    setToggledActivities({});
    setCompKey((prev) => prev + 1);
  }, []);

  const onSaveJSA = useCallback(() => {
    if (isJSASectionDisabled || !jsaV2Updated) return;

    setUpdating(true);

    const formData = buildJsaFormData(swmsData, activitiesData);

    dispatch(saveSwmsForm(jobId, swmsId, formData))
      .then((message) => {
        ignoreNextPropSyncRef.current = true;
        originalActivitiesRef.current = cloneJsaActivities(activitiesData);
        if (onJsaActivitiesChange) {
          onJsaActivitiesChange(cloneJsaActivities(activitiesData));
        }
        setActivitiesData(cloneJsaActivities(activitiesData));
        setUpdating(false);
        setJsaV2Updated(false);
        setModifiedActs({});
        setToggledActivities({});
        notification.success({
          message: Strings.success_title || "Success",
          description: message || "JSA saved successfully",
          className: "ant-success"
        });
        if (onClose) onClose();
      })
      .catch((err) => {
        console.log(err);
        setUpdating(false);
        notification.error({
          message: Strings.error_title || "Error",
          description: err || Strings.generic_error || "Failed to save JSA",
          className: "ant-error"
        });
      });
  }, [isJSASectionDisabled, jsaV2Updated, activitiesData, swmsData, jobId, swmsId, dispatch, onClose, onJsaActivitiesChange]);

  return {
    activitiesData,
    compKey,
    isJSASectionDisabled,
    jsaV2Updated,
    onActivityAdd,
    onCancelChanges,
    onDrop,
    onJSADataAdd,
    onJSADataDelete,
    onMoveActivity,
    onMoveRouter,
    onRiskSave,
    onSaveJSA,
    onV2ActUpdate,
    updating,
  };
}